import { Alert } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { UUIDString } from "../../../../common/service/server/Types";
import { Loading } from "../../../components";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { usePrimaryOrganizationId } from "../../../hooks";
import { EXCLUDE_GROUP_EMPLOYMENT } from "../../../hooks/useCollectionGroups";
import { useProgressKeys } from "../../../hooks/useProgressKeys";
import { useGetCompliantContext } from "../../../root/GetCompliantContext";
import { Routes } from "../../../root/routes";
import { useNextStep } from "../../../surveys/steps/GetCompliantSteps";
import { DataRecipientMultiselect } from "@truevault/aquilo";
import { GetCompliantFooter } from "../ProgressFooter";
import { useDataRecipientAssociation } from "../../../hooks/useVendorAssociation";

export const VendorAssociationForGroup: React.FC = () => {
  const { progress } = useGetCompliantContext();

  // Variables
  const params = useParams() as { groupId: UUIDString };
  const orgId = usePrimaryOrganizationId();

  const { group, fetched, requests, multiselect, error } = useDataRecipientAssociation(
    orgId,
    params.groupId,
    EXCLUDE_GROUP_EMPLOYMENT,
  );

  // State
  const isOnlyGroup = fetched?.collectionGroups?.length == 1;

  const keys = useMemo(
    () => fetched?.collectionGroups?.map((g) => `vendor-association-${g.id}`),
    [fetched?.collectionGroups],
  );

  const { update: updateProgress } = useProgressKeys(orgId, keys);

  const onNext = async () => {
    const key = `vendor-association-${group.id}`;
    await updateProgress({ keyId: key, progress: "DONE" });
  };

  const [handleAutoNext] = useNextStep("DataRecipients.VendorAssociation", async () => {
    await onNext();
  });

  const nextUrl = isOnlyGroup ? undefined : Routes.getCompliant.dataRecipients.vendorAssociation;
  const prevUrl = isOnlyGroup ? undefined : Routes.getCompliant.dataRecipients.vendorAssociation;
  const noProgress = isOnlyGroup ? false : true;

  // automatically associate everything and go to the next screen if there's only a single group
  useEffect(() => {
    if (isOnlyGroup && group?.id) {
      handleAutoNext();
    }
  }, [isOnlyGroup, group, handleAutoNext]);

  if (isOnlyGroup) {
    return (
      <GetCompliantPageWrapper currentProgress={progress}>
        <PageHeader titleContent="" />
        <Loading />
      </GetCompliantPageWrapper>
    );
  }

  if (error) {
    return (
      <GetCompliantPageWrapper currentProgress={progress}>
        <PageHeader titleContent="" />
        <Alert color="error">There was an error while fetching this data. Please try again.</Alert>
      </GetCompliantPageWrapper>
    );
  }

  const isFinished = true;

  return (
    <GetCompliantPageWrapper
      currentProgress={progress}
      requests={[requests.collectionGroups, requests.dataMap, requests.sources]}
      progressFooter={
        <GetCompliantFooter
          presentStep="DataRecipients.VendorAssociation"
          nextUrl={nextUrl}
          nextOnClick={onNext}
          nextDisabled={!isFinished}
          prevUrl={prevUrl}
          noProgress={noProgress}
        />
      }
    >
      <PageHeader titleContent="Associations" />

      <h4 className="intermediate">
        Which of your Data Recipients process information about {group?.name}?
      </h4>

      <DataRecipientMultiselect {...multiselect} />
    </GetCompliantPageWrapper>
  );
};
